"use client";

import { ReactNode } from "react";

import { Button, Divider } from "@/components/ui";

import { cx } from "@/lib/tailwind-utils";

export function CategorySidebarButton({
  title,
  isActive,
  onClick,
  className,
  depth = 0,
}: {
  title: string;
  isActive: boolean;
  onClick: () => void;
  className?: string;
  /** 0 for top-level categories. Indents the button to expose the hierarchy. */
  depth?: number;
}) {
  // Tailwind's !px-5 emits padding-left: 1.25rem !important, which would beat any
  // inline style — pick the depth-correct class instead and only apply !pl-5 at depth 0.
  const depthClass =
    depth === 0
      ? "!pl-5"
      : depth === 1
        ? "!pl-8"
        : depth === 2
          ? "!pl-11"
          : "!pl-14";
  return (
    <Button
      variant={isActive ? "primary" : "secondary"}
      onClick={onClick}
      className={cx(
        "w-full justify-start !pr-5 !py-2.5 text-left",
        depthClass,
        depth === 0 ? "!text-h4" : "!text-body",
        className,
      )}
    >
      {depth > 0 && (
        <span className="mr-2 text-foreground-secondary" aria-hidden="true">
          └
        </span>
      )}
      <span>{title}</span>
    </Button>
  );
}

export function CategorySidebarLayout({
  sidebar,
  children,
  className,
}: {
  sidebar: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cx(
        "flex flex-col gap-4 md:flex-row md:items-start xl:min-w-[60rem]",
        className,
      )}
    >
      <aside className="min-h-0 md:sticky md:top-20 md:max-h-[calc(100vh-2rem)] md:w-[15rem] md:shrink-0 md:overflow-y-auto md:pr-1">
        <div className="space-y-3">{sidebar}</div>
      </aside>

      <Divider className="my-0 md:hidden" />

      <div className="min-w-0 flex-1 scroll-mt-20">{children}</div>
    </div>
  );
}
